import React from 'react';
import {
  Box,
  Button,
  TextField,
  InputAdornment,
  useTheme
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

export type TouchModeInputField = string;

export type TouchModeInputProps = {
  field: TouchModeInputField;
  value: string;
  onChange: (field: TouchModeInputField, value: string) => void;
  onFocus: (
    field: string,
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  onBlur: (field: TouchModeInputField) => void;
  onKeyDown: (field: TouchModeInputField, e: React.KeyboardEvent) => void;
  onIncrement: () => void;
  onDecrement: () => void;
  unit?: string;
  extraInputProps?: Record<string, string | number | boolean>;
  disabled?: boolean;
};

export const TouchModeInput: React.FC<TouchModeInputProps> = ({
  field,
  value,
  onChange,
  onFocus,
  onBlur,
  onKeyDown,
  onIncrement,
  onDecrement,
  unit = '',
  extraInputProps = {},
  disabled = false
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const endAdornment = unit ? (
    <InputAdornment position="end">{unit}</InputAdornment>
  ) : undefined;

  return (
    <Box
      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%' }}
    >
      <Button
        variant="outlined"
        onClick={onDecrement}
        disabled={disabled}
        tabIndex={-1}
        sx={{
          minWidth: 32,
          height: 32,
          p: 0,
          borderRadius: 1,
          color: isDarkMode ? 'rgba(239, 154, 154, 1)' : 'error.main',
          backgroundColor: isDarkMode
            ? 'rgba(244, 67, 54, 0.15)'
            : 'transparent',
          border: '1px solid',
          borderColor: isDarkMode ? 'rgba(244, 67, 54, 0.6)' : 'error.main',
          '&:hover': {
            backgroundColor: isDarkMode
              ? 'rgba(244, 67, 54, 0.25)'
              : 'rgba(244, 67, 54, 0.04)',
            borderColor: isDarkMode ? 'rgba(244, 67, 54, 0.8)' : 'error.dark'
          }
        }}
      >
        <RemoveIcon fontSize="small" />
      </Button>

      <TextField
        size="small"
        fullWidth
        value={value}
        onChange={e => onChange(field, e.target.value)}
        onFocus={e => onFocus(field, e)}
        onBlur={() => onBlur(field)}
        onKeyDown={e => onKeyDown(field, e)}
        disabled={disabled}
        inputProps={{
          inputMode: 'decimal',
          'data-field': field,
          style: { textAlign: 'right' },
          ...extraInputProps
        }}
        InputProps={{
          endAdornment
        }}
      />

      <Button
        variant="outlined"
        onClick={onIncrement}
        disabled={disabled}
        tabIndex={-1}
        sx={{
          minWidth: 32,
          height: 32,
          p: 0,
          borderRadius: 1,
          color: isDarkMode ? 'rgba(129, 199, 132, 1)' : 'success.main',
          backgroundColor: isDarkMode
            ? 'rgba(76, 175, 80, 0.15)'
            : 'transparent',
          border: '1px solid',
          borderColor: isDarkMode ? 'rgba(76, 175, 80, 0.6)' : 'success.main',
          '&:hover': {
            backgroundColor: isDarkMode
              ? 'rgba(76, 175, 80, 0.25)'
              : 'rgba(76, 175, 80, 0.04)',
            borderColor: isDarkMode ? 'rgba(76, 175, 80, 0.8)' : 'success.dark'
          }
        }}
      >
        <AddIcon fontSize="small" />
      </Button>
    </Box>
  );
};
